// Compilateur — partie SYNTAXE : packs (données) → SyntaxTable (consommée par kernel/syntax.js).
// La fusion est additive : un pack ajoute des symboles, des nœuds, des fonctions.
// Redéfinir autrement un symbole déjà pris est une ERREUR de build, sauf `override: true`.

import { num } from '../kernel/ast.js';

export function buildSyntaxTable(packs) {
  const diagnostics = [];
  const err = (pack, msg) => diagnostics.push({ level: 'error', pack, msg });
  const warn = (pack, msg) => diagnostics.push({ level: 'warning', pack, msg });

  const lex = {
    letters: '',
    subscripts: '',
    decimal: [],
    symbols: {},
    names: {},
    greekLatex: {},
    formulaAtoms: [],
  };
  const syntax = {
    infix: {},
    prefix: {},
    postfix: {},
    groups: {},
    prefixPrimary: {},
    words: {},
    functions: {},
    constants: {},
    implicit: null,
    argSep: ';',
  };
  const nodes = {};
  const owner = {}; // "fixity:symbol" → pack id, pour les conflits

  for (const pack of packs) {
    const L = pack.lexicon || {};
    if (L.letters) lex.letters += L.letters;
    if (L.subscripts) lex.subscripts += L.subscripts;
    // chiffres exposants « ⁰¹…⁹ⁿ⁻ » → valeur de l'exposant (« x⁵ », « 10⁻³ ») ; signes diacritiques collés à un nom (« Â »)
    if (L.superscripts) lex.superscripts = { ...(lex.superscripts || {}), ...L.superscripts };
    if (L.superscriptNode) lex.superscriptNode = L.superscriptNode;
    if (L.combining) lex.combining = (lex.combining || '') + L.combining;
    if (L.decimal) for (const d of L.decimal) if (!lex.decimal.includes(d)) lex.decimal.push(d);
    if (L.argSep) syntax.argSep = L.argSep;
    if (L.vectorMark) lex.vectorMark = L.vectorMark; // « AB⃗ » : un vecteur, lu comme un seul identifiant
    // analyse : l'infini « ∞ » (lu comme la constante +∞, « −∞ » est son opposé), la marque de dérivée « ′ »
    // (« f′(x) » est UN identifiant), les variables d'analyse (dérivées, limites) dans l'ordre de préférence
    if (L.infinity) lex.infinity = L.infinity;
    if (L.prime) lex.prime = L.prime;
    if (L.calculusVars) lex.calculusVars = [...L.calculusVars];
    if (L.indeterminate) lex.indeterminate = { ...L.indeterminate };
    Object.assign(lex.greekLatex, L.greekLatex || {});
    // atomes de formule (ex. éléments chimiques « H », « Na », « Cl ») : une suite collée d'atomes
    // et d'indices (« H₂O », « NaCl ») est lue comme UN identifiant au lieu d'un produit de lettres.
    for (const a of L.formulaAtoms || [])
      if (!lex.formulaAtoms.includes(a)) lex.formulaAtoms.push(a);

    for (const n of pack.nodes || []) {
      if (nodes[n.type] && !n.override)
        err(pack.id, `type de nœud « ${n.type} » déjà défini par ${nodes[n.type].pack}`);
      nodes[n.type] = { ...n, pack: pack.id };
    }

    for (const g of pack.groups || []) {
      const id = `group:${g.open}`;
      lex.symbols[g.open] = id;
      // même signe pour ouvrir et fermer (« |…| ») : le même identifiant
      const close = g.close === g.open ? id : `close:${g.close}`;
      if (g.close !== g.open) lex.symbols[g.close] = close;
      syntax.groups[id] = close;
      // groupe qui fait un nœud (« |x| » = valeur absolue)
      if (g.node) (syntax.groupNodes ??= {})[id] = g.node;
    }

    for (const op of pack.operators || []) {
      const surfaces = op.symbols || op.words || [];
      const opId = op.id;
      for (const s of surfaces) {
        const k = `${op.fixity}:${s}`;
        if (owner[k] && owner[k].id !== opId && !op.override) {
          err(
            pack.id,
            `« ${s} » (${op.fixity}) est déjà l’opérateur « ${owner[k].id} » du pack ${owner[k].pack}`,
          );
          continue;
        }
        owner[k] = { id: opId, pack: pack.id };
        if (op.words) {
          lex.names[s] = 'word';
          syntax.words[s] = opId;
        } else {
          if (lex.symbols[s] && lex.symbols[s] !== opId)
            err(pack.id, `le symbole « ${s} » désigne déjà « ${lex.symbols[s]} »`);
          lex.symbols[s] = opId;
        }
      }
      const entry = { prec: op.prec, node: op.node, pack: pack.id };
      if (op.fixity === 'infix') {
        syntax.infix[opId] = {
          ...entry,
          assoc: op.assoc || 'left',
          wrapRight: op.wrapRight,
          chain: !!op.chain,
        };
      } else if (op.fixity === 'prefix') {
        syntax.prefix[opId] = entry;
        if (!syntax.infix[opId]) syntax.prefixPrimary[opId] = true;
      } else if (op.fixity === 'postfix') {
        syntax.postfix[opId] = { ...entry, arg: op.arg != null ? num(op.arg) : undefined };
      } else err(pack.id, `fixité inconnue « ${op.fixity} » pour ${opId}`);
    }

    if (pack.implicitMultiplication) syntax.implicit = { ...pack.implicitMultiplication };

    for (const f of pack.functions || []) {
      if (syntax.functions[f.name] && !f.override)
        err(pack.id, `fonction « ${f.name} » déjà définie`);
      syntax.functions[f.name] = {
        node: f.node || 'Call',
        latex: f.latex,
        eval: f.eval,
        prec: f.prec,
        pack: pack.id,
      };
      lex.names[f.name] = 'function';
    }
    for (const c of pack.constants || []) {
      // « +inf » : l'infini (valeur JSON-sûre dans le moteur généré, Infinity dans le noyau — voir constValue)
      syntax.constants[c.name] = { value: c.value, latex: c.latex, pack: pack.id };
      lex.names[c.name] = 'constant';
    }
  }

  // contrôles croisés
  const used = new Set(
    [
      ...Object.values(syntax.infix).map((o) => o.node),
      ...Object.values(syntax.prefix).map((o) => o.node),
      ...Object.values(syntax.postfix).map((o) => o.node),
      ...Object.values(syntax.infix)
        .map((o) => o.wrapRight)
        .filter(Boolean),
      ...Object.values(syntax.functions).map((f) => f.node),
      syntax.implicit?.node,
    ].filter(Boolean),
  );
  for (const t of used)
    if (!nodes[t]) err('*', `l’opérateur produit le nœud « ${t} », qu’aucun pack ne définit`);
  for (const [t, n] of Object.entries(nodes)) {
    if (!n.print) warn(n.pack, `le nœud « ${t} » n’a pas de stratégie d’impression`);
    if (n.print === 'template' && (!n.text || !n.latex))
      err(n.pack, `le nœud « ${t} » (template) doit avoir text et latex`);
  }
  // un nom de fonction ne doit pas être une suite de lettres qui masquerait des produits usuels
  for (const name of Object.keys(lex.names)) {
    if (name.length === 1 && lex.names[name] !== 'constant' && lex.names[name] !== 'function')
      warn('*', `le nom « ${name} » d’une seule lettre masque une variable`);
  }

  // séparateur d'arguments (« C(4 ; 2) ») : un symbole du lexique dès qu'il existe
  if (syntax.argSep && !lex.symbols[syntax.argSep]) lex.symbols[syntax.argSep] = syntax.argSep;
  lex.symbolList = Object.keys(lex.symbols).sort((a, b) => b.length - a.length);
  lex.nameList = Object.keys(lex.names).sort((a, b) => b.length - a.length);
  lex.formulaAtoms.sort((a, b) => b.length - a.length);
  return { table: { lex, syntax, nodes }, diagnostics };
}
